import { forwardRef } from "react"
import { VariantProps } from 'class-variance-authority'
import { twMerge } from "tailwind-merge"
import { cn } from "../../utils/cn"
import { textAreaVariants } from "./styles"
import { ErrorMessage } from "@/common/components/ErrorMessage"
import { replacePeriodToUnderscore } from "@/common/utils/transformers";

export interface TextAreaProps extends React.HTMLAttributes<HTMLTextAreaElement>, VariantProps<typeof textAreaVariants> {
  label?: string;
  disabled?: boolean;
  maxLength?: number;
  RightIcon?: JSX.Element;
  LeftIcon?: JSX.Element;
  placeholder?: string;
  errorMessage?: string;
  value?: string | null;
  onIconClick?: React.MouseEventHandler<HTMLDivElement>;
  rows?: number;
  markAsRequired?: boolean;
  autoResize?: boolean;
  name?: string;
  dataTestid?: string;
  textareaClassName?: string;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  label,
  variant,
  className,
  disabled = false,
  maxLength,
  placeholder,
  errorMessage,
  value,
  onIconClick,
  rows = 4,
  markAsRequired,
  autoResize,
  name,
  dataTestid,
  textareaClassName,
  ...props
}, ref) => {

  return (
    <div className={className}>
      <label className="relative flex flex-col items-start justify-center">
        {label && (
          <p className={twMerge('text-xs mb-1')}>
            {label}{markAsRequired && <span className="text-danger-soft"> *</span>}
          </p>
        )}

        <textarea
          ref={ref}
          rows={rows}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value as string | undefined}
          className={cn(
            "resize-none placeholder:text-white-300",
            textAreaVariants({ variant: disabled ? 'disabled' : variant }),
            autoResize && 'max-h-[280px]',
            textareaClassName
          )}
          onInput={(event) => {
            if (autoResize) {
              event.currentTarget.style.height = '5px';
              event.currentTarget.style.height = (event.currentTarget.scrollHeight) + 3 + 'px';
            }
          }}
          name={name}
          data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_input' : '')}
          {...props}
        />

        {maxLength && <span className="absolute right-2 bottom-1 text-[10px] text-white-300">{value?.length}/{maxLength}</span>}
      </label>
      {!!errorMessage?.length && <ErrorMessage name={name} message={errorMessage} />}
    </div>
  );
});

export default TextArea;
